/**
 * Button — the portal's single button component.
 *
 * The visual recipe lives entirely in button.module.css, so no screen needs to
 * repeat padding/radius/hover rules or invent another `*-btn` class. Renders a
 * real <button> by default; pass `as="a"` with `href` for navigation that should
 * still look like a button.
 */

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import styles from './button.module.css';

export type ButtonVariant = 'primary' | 'gold' | 'ghost' | 'danger' | 'dangerQuiet' | 'success' | 'link';
type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container width — dialog footers, narrow forms. */
  block?: boolean;
  className?: string;
  children?: ReactNode;
}

// `ref` is declared explicitly because React 19 passes it as an ordinary prop to
// function components, but the HTML attribute types do not include it.
type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    as?: 'button';
    ref?: Ref<HTMLButtonElement>;
  };

type ButtonAsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    as: 'a';
    ref?: Ref<HTMLAnchorElement>;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

/** Compose the module's local class names; `md` is the base size, so it adds none. */
function classesFor(variant: string, size: ButtonSize, block: boolean, extra?: string) {
  return [
    styles.base,
    styles[variant],
    size !== 'md' ? styles[size] : null,
    block ? styles.block : null,
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

export function Button(props: ButtonProps) {
  const { variant = 'primary', size = 'md', block = false, className, children, ...rest } = props;
  const composed = classesFor(variant, size, block, className);

  // `as` is a rendering switch, not a DOM attribute, so it is dropped from the
  // props spread rather than forwarded.
  if (rest.as === 'a') {
    const { as: _anchorTag, ...anchorProps } = rest as ButtonAsAnchor;
    void _anchorTag;
    return (
      <a className={composed} {...anchorProps}>
        {children}
      </a>
    );
  }

  const { as: _buttonTag, type = 'button', ...buttonProps } = rest as ButtonAsButton;
  void _buttonTag;
  return (
    <button type={type} className={composed} {...buttonProps}>
      {children}
    </button>
  );
}
